import React, { useState, useEffect } from 'react';
import { navLinks as importedNavLinks } from '../../data/dreamlandData';
import { Phone, Menu, X, ChevronUp } from 'lucide-react';
import BookingModal from './BookingModal';
import { rooms } from '../../data/dreamlandData';
import DreamlandLogoremovebg from '../../assets/logos/DreamlandLogo-removebg.webp';

// ✅ Safe fallback in case dreamlandData hasn't initialized yet during HMR
const navLinks = importedNavLinks || [];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [showCallOptions, setShowCallOptions] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
      setShowScrollTop(window.scrollY > 600);
    };
    const handleClickOutside = () => setShowCallOptions(false);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleNavClick = (e, href) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const id = href.replace('/#', '');
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileOpen(false);
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <>
      <nav
        className={`sticky top-0 z-40 transition-all duration-500 ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">

            {/* LOGO */}
            <a href="/" className="flex items-center gap-2 min-w-0 shrink-0">
              <img
                src={DreamlandLogoremovebg}
                alt="Dreamland Inn Logo"
                className="h-10 sm:h-14 lg:h-16 w-auto object-contain"
              />
              <div className="flex flex-col leading-none">
                <span className="text-base sm:text-lg lg:text-xl font-bold font-['Playfair_Display'] text-[#1B3D6E]">
                  Dreamland
                </span>
                <span className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#F5C518] font-semibold">
                  Inn
                </span>
              </div>
            </a>

            {/* DESKTOP NAV LINKS */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="px-4 py-2 text-sm font-bold text-[#1F2937] hover:text-[#1B3D6E] transition relative group"
                >
                  {link.label}
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[#F5C518] group-hover:w-3/4 transition-all duration-300" />
                </a>
              ))}
            </div>

            {/* DESKTOP RIGHT */}
            <div className="hidden lg:flex items-center gap-3 relative">
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCallOptions(!showCallOptions);
                  }}
                  className="p-2 rounded-full border border-[#2478C8] text-[#2478C8] hover:bg-[#2478C8] hover:text-white transition"
                >
                  <Phone size={18} />
                </button>
                {showCallOptions && (
                  <div className="absolute right-0 mt-2 w-52 bg-white shadow-lg rounded-md border z-50">
                    <a
                      href="tel:+919423296526"
                      className="block px-4 py-2 text-sm hover:bg-[#EEF6FF] hover:text-[#1B3D6E]"
                    >
                      +91 9423296526
                    </a>
                    <a
                      href="tel:+919404992515"
                      className="block px-4 py-2 text-sm hover:bg-[#EEF6FF] hover:text-[#1B3D6E]"
                    >
                      +91 9404992515
                    </a>
                  </div>
                )}
              </div>

              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="px-6 py-2.5 bg-[#1B3D6E] text-white text-sm font-semibold rounded transition-all duration-200 hover:bg-[#F5C518] hover:text-[#1B3D6E]"
              >
                Book Now
              </button>
            </div>

            {/* MOBILE RIGHT */}
            <div className="lg:hidden flex items-center gap-1.5 shrink-0">

              {/* Phone */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCallOptions(!showCallOptions);
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-[#2478C8] text-[#2478C8] hover:bg-[#2478C8] hover:text-white transition"
                >
                  <Phone size={15} />
                </button>
                {showCallOptions && (
                  <div className="absolute right-0 mt-2 w-44 bg-white shadow-lg rounded-md border z-50">
                    <a
                      href="tel:+919423296526"
                      className="block px-3 py-2 text-xs hover:bg-[#EEF6FF] hover:text-[#1B3D6E]"
                    >
                      +91 9423296526
                    </a>
                    <a
                      href="tel:+919404992515"
                      className="block px-3 py-2 text-xs hover:bg-[#EEF6FF] hover:text-[#1B3D6E]"
                    >
                      +91 9404992515
                    </a>
                  </div>
                )}
              </div>

              {/* Book Now */}
              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="px-3 py-1.5 bg-[#1B3D6E] text-white text-xs font-semibold rounded-full transition-all duration-200 hover:bg-[#F5C518] hover:text-[#1B3D6E] whitespace-nowrap"
              >
                Book Now
              </button>

              {/* Hamburger */}
              <button
                onClick={() => setIsMobileOpen(!isMobileOpen)}
                className="w-8 h-8 flex items-center justify-center text-[#1B3D6E] hover:text-[#2478C8]"
              >
                {isMobileOpen ? <X size={22} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* MOBILE MENU DROPDOWN */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ${
            isMobileOpen ? 'max-h-[500px] border-t border-gray-100' : 'max-h-0'
          }`}
        >
          <div className="px-4 py-4 bg-white space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="block px-4 py-3 text-sm font-medium text-[#1F2937] hover:text-[#1B3D6E] hover:bg-[#EEF6FF] rounded"
              >
                {link.label}
              </a>
            ))}

            <button
              onClick={() => {
                setIsBookingModalOpen(true);
                setIsMobileOpen(false);
              }}
              className="block w-full text-center px-4 py-3 mt-2 bg-[#1B3D6E] text-white text-sm font-semibold rounded transition-all duration-200 hover:bg-[#F5C518] hover:text-[#1B3D6E]"
            >
              Book Now
            </button>

            <div className="flex flex-col gap-2 px-4 pt-3 border-t mt-3">
              <a href="tel:+919423296526" className="flex items-center gap-2 text-sm">
                <Phone size={14} className="text-[#2478C8]" />
                +91 9423296526
              </a>
              <a href="tel:+919404992515" className="flex items-center gap-2 text-sm">
                <Phone size={14} className="text-[#2478C8]" />
                +91 9404992515
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* SCROLL TO TOP */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-24 right-6 z-50 w-10 h-10 rounded-full bg-[#1B3D6E] text-white flex items-center justify-center shadow-lg transition-all duration-200 hover:bg-[#F5C518] hover:text-[#1B3D6E]"
        >
          <ChevronUp size={20} />
        </button>
      )}

      {/* ✅ Now passes rooms as prop — BookingModal no longer imports rooms itself */}
      {isBookingModalOpen && (
        <BookingModal
          onClose={() => setIsBookingModalOpen(false)}
          rooms={rooms}
        />
      )}
    </>
  );
};

export default Navbar;